import { useQuery } from '@tanstack/react-query';
import { Action, Subject } from '@erp/authz';
import { SystemEntity } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useCan } from '@/hooks/useCan';

/** `tasks.list` caps a page at 100. An invoice's anchored tasks fit in one page. */
const INVOICE_TASKS_LIMIT = 100;

/**
 * Tasks anchored to this AP invoice, newest activity first. Callers who
 * cannot read tasks get an empty result and the query stays disabled.
 */
export function useInvoiceTasks(invoiceId: string) {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const query = useQuery({
    ...orpc.tasks.list.queryOptions({
      input: {
        anchorEntityType: SystemEntity.AP_INVOICE,
        anchorEntityId: invoiceId,
        limit: INVOICE_TASKS_LIMIT,
        offset: 0,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      },
    }),
    enabled: canReadTasks && invoiceId.length > 0,
  });

  const tasks = query.data?.tasks ?? [];
  return {
    tasks,
    total: query.data?.pagination.total ?? 0,
    hasMore: query.data?.pagination.hasMore ?? false,
    canReadTasks,
    isLoading: canReadTasks && query.isLoading,
    error: canReadTasks ? query.error : null,
  };
}
